import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Observable, map, of, tap } from 'rxjs';
import { IconsCached } from './icon.model';
import { differenceInDays } from 'date-fns';

@Injectable({
  providedIn: 'root',
})
export class IconFetchService {
  #httpClient = inject(HttpClient);
  #sanitizer = inject(DomSanitizer);
  #storageKey = 'roomex-icons';
  #iconsCached: IconsCached;

  constructor() {
    this.#iconsCached = this.#retrieveIconsCached();

    if (this.#isCacheExpired()) {
      this.resetCache();
    }
  }

  resetCache(): void {
    this.#iconsCached = { lastUpdate: new Date() };
    localStorage?.setItem(this.#storageKey, JSON.stringify(this.#iconsCached));
  }

  getIcon(iconUrl: string): Observable<SafeHtml> {
    const iconCached: SafeHtml = this.#iconsCached[iconUrl];
    if (iconCached) {
      return of(iconCached).pipe(map((response: string) => this.#sanitizer.bypassSecurityTrustHtml(response)));
    }

    return this.#httpClient.get(iconUrl, { responseType: 'text' }).pipe(
      tap((icon: SafeHtml) => this.#saveIconInStorage(iconUrl, icon)),
      map((response: string) => this.#sanitizer.bypassSecurityTrustHtml(response))
    );
  }

  #saveIconInStorage(iconUrl: string, icon: SafeHtml): void {
    this.#iconsCached[iconUrl] = icon;
    this.#iconsCached.lastUpdate = new Date();
    localStorage?.setItem(this.#storageKey, JSON.stringify(this.#iconsCached));
  }

  #retrieveIconsCached(): IconsCached {
    const iconsCached = localStorage?.getItem(this.#storageKey);
    return iconsCached ? JSON.parse(iconsCached) : { lastUpdate: new Date() };
  }

  #isCacheExpired(): boolean {
    const lastUpdate = new Date(this.#iconsCached.lastUpdate);
    const today = new Date();
    return differenceInDays(today, lastUpdate) >= 7;
  }
}
